// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    Action,
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import { getActionColumnProps } from 'common/helpers/list';
import * as React from 'react';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as haActions from 'common/modules/highAvailability/actions';
import List from 'common/components/List/List';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { formatTableDate } from 'common/date';
import { formatDuration } from 'common/helpers/duration';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { connect } from 'react-redux';
import {
    HighAvailabilityStatus,
    HighAvailabilityEntityStatus,
    IHighAvailabilityActivity,
    IHighAvailabilityActivityEntity,
} from 'common/api/resources/HighAvailability';
import { HIGH_AVAILABILITY_HISTORY } from 'admin/highAvailability/constants/test';
import { HighAvailabilityOperations } from 'admin/highAvailability/components/HistoryTable/HighAvailabilityOperations';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { ICONS } from 'common/constants';
import { TASK_STATUS } from 'common/api/resources/Task';
import { IListRow } from 'common/list';
import HighAvailabilityEntityRow from 'admin/highAvailability/components/HistoryTable/HighAvailabilityEntityRow';
import { dataCySelector } from 'common/tests/selectors';
import CopyText from 'common/containers/CopyText/CopyText';
import useQuery from 'common/hooks/useQuery';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';
import { PropsValue } from 'react-select';
import {
    ISelectRequiredOption,
    Loader,
} from 'common/components';
import { IComputeResourceFilterOptionType } from 'admin/common/components/Filters/ComputeResourceFilter/ComputeResourceFilter';
import { computeResources } from 'common/api/resources/ComputeResource';
import {
    HighAvailabilityHistoryStatusComponent,
    statusMappings as activityStatusMappings,
} from 'admin/highAvailability/components/HistoryTable/HighAvailabilityHistoryStatus';
import { COLORS } from 'common/theme';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import { COLUMN_WIDTHS } from 'admin/highAvailability/components/HistoryTable/columnWidths';
import { HistoryTableWrapper } from 'admin/highAvailability/components/HistoryTable/Styles';

const isMoveBackTaskInProgress = (p: IHighAvailabilityActivityEntity) =>
    !!p.move_back_task &&
    !p.is_migrated_back &&
    p.move_back_task.status !== TASK_STATUS.FAILED &&
    p.move_back_task.status !== TASK_STATUS.DONE &&
    p.move_back_task.status !== TASK_STATUS.DONE_WITH_ERRORS &&
    p.move_back_task.status !== TASK_STATUS.CANCELED;

const DONE_STATUSES: HighAvailabilityStatus[] = [
    HighAvailabilityStatus.COMPLETED,
    HighAvailabilityStatus.COMPLETED_WITH_ERRORS,
    HighAvailabilityStatus.FAILED,
    HighAvailabilityStatus.CANCELLED,
];

const columns = [{
    key: 'colId',
    width: COLUMN_WIDTHS.id,
    title: <Translate content="highAvailability.activities.list.id" />,
}, {
    key: 'colComputeResource',
    width: COLUMN_WIDTHS.computeResource,
    title: <Translate content="highAvailability.activities.list.computeResource" />,
}, {
    key: 'colStatus',
    width: COLUMN_WIDTHS.status,
    title: <Translate content="highAvailability.activities.list.status" />,
}, {
    key: 'colStartedAt',
    width: COLUMN_WIDTHS.startedAt,
    title: <Translate content="highAvailability.activities.list.startedAt" />,
}, {
    key: 'colDuration',
    width: COLUMN_WIDTHS.duration,
    title: <Translate content="highAvailability.activities.list.duration" />,
}, {
    key: 'colProcesses',
    width: COLUMN_WIDTHS.processes,
    title: <Translate content="highAvailability.activities.list.processes" />,
}, {
    key: 'colFailoverDomain',
    width: COLUMN_WIDTHS.failoverDomain,
    title: <Translate content="highAvailability.activities.list.failoverDomain" />,
},
getActionColumnProps({ width: COLUMN_WIDTHS.actions }),
];

interface IHighAvailabilityHistoryRequestFiltersQuery {
    status?: string;
    source_compute_resource_id?: string;
}

interface IHighAvailabilityHistoryTableProps extends RouteComponentProps {
    emptyView: React.ReactElement;
}

export type HighAvailabilityHistoryTableProps =
    IHighAvailabilityHistoryTableProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const HighAvailabilityHistoryTable: React.FC<HighAvailabilityHistoryTableProps> = ({
    activities,
    emptyView,
    isLoadingActivities,
    isMovingBack,
    history,
    highAvailabilityActions: {
        getHighAvailabilities,
        moveBackHighAvailabilityEntities,
    },
}) => {
    const [selection, setSelection] = React.useState<string[]>([]);
    const [processSelection, setProcessSelection] = React.useState<IHighAvailabilityActivityEntity[]>([]);
    const [expandedRows, setExpandedRows] = React.useState<string[]>([]);
    const [filters, setFilters] = useQuery<IHighAvailabilityHistoryRequestFiltersQuery>();
    const [selectedStatus, setSelectedStatus] = React.useState<ISelectRequiredOption>();
    const [selectedCr, setSelectedCr] = React.useState<IComputeResourceFilterOptionType>();

    const translate = useTranslate();

    const getQueryFilters = () => ({
        filters: {
            status: filters.status,
            source_compute_resource_id: filters.source_compute_resource_id
                ? Number(filters.source_compute_resource_id)
                : undefined,
        },
    });

    const isFirstLoading = useRequestCancellationEffect(
        async () => {
            await getHighAvailabilities(getQueryFilters());
        },
        [filters]
    );

    React.useEffect(() => {
        if (filters.status && filters.status !== selectedStatus?.value) {
            const statusValue = filters.status as HighAvailabilityStatus;

            setSelectedStatus({
                label: translate(activityStatusMappings[statusValue]) as string,
                value: statusValue,
            });
        }

        if (!filters.status && selectedStatus) {
            setSelectedStatus(undefined);
        }

        if (
            filters.source_compute_resource_id &&
            Number(filters.source_compute_resource_id) !== Number(selectedCr?.value)
        ) {
            const crId = filters.source_compute_resource_id;

            (async () => {
                const {
                    data: {
                        data: { id, name, host },
                    },
                } = await computeResources.item(Number(crId));

                setSelectedCr({ value: id.toString(), label: name, host });
            })();
        }

        if (!filters.source_compute_resource_id && selectedCr) {
            setSelectedCr(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const filteredCount = React.useMemo(
        () => Object.values(filters).filter((value) => !!value).length,
        [filters]
    );

    const handleExpandedRowsChange = (rows: string[]) => setExpandedRows(rows);

    const loadPaginated = (page: number) => getHighAvailabilities({ page, ...getQueryFilters() });

    const selectedActivities = React.useMemo(() =>
        activities.data.filter(activity => selection.includes(activity.id.toString())),
    [activities, selection]
    );

    const activityEntities = React.useMemo(() =>
        selectedActivities.flatMap(a => a.entities?.data ?? []),
    [selectedActivities]
    );

    const allSelectedEntities = React.useMemo(() => {
        const processIds = new Set(processSelection.map(p => p.id));
        return [...processSelection, ...activityEntities.filter(e => !processIds.has(e.id))];
    }, [processSelection, activityEntities]);

    const canMoveBack = React.useMemo(() =>
        (processSelection.length > 0 || selectedActivities.length > 0) &&
        (
            selectedActivities.some(a => a.entities === undefined) ||
            allSelectedEntities.some(
                p => p.status === HighAvailabilityEntityStatus.COMPLETED
                    && !p.is_migrated_back
                    && !isMoveBackTaskInProgress(p)
            )
        ),
    [allSelectedEntities, selectedActivities, processSelection]
    );

    const canRetry = React.useMemo(() =>
        (processSelection.length > 0 || selectedActivities.length > 0) &&
        processSelection.every(p => p.status === HighAvailabilityEntityStatus.FAILED) &&
        selectedActivities.every(a => a.status === HighAvailabilityStatus.FAILED),
    [processSelection, selectedActivities]
    );

    const retryTooltip = React.useMemo((): React.ReactNode => {
        if (canRetry) {
            return <Translate content="highAvailability.activities.operations.retryConfirmation.tooltip" />;
        }
        if (processSelection.length === 0 && selectedActivities.length === 0) {
            return <Translate content="highAvailability.activities.operations.retryDisabledNothingSelected" />;
        }
        if (processSelection.some(p => p.status !== HighAvailabilityEntityStatus.FAILED)) {
            return <Translate content="highAvailability.activities.operations.retryDisabledNonFailedProcesses" />;
        }
        if (selectedActivities.some(a => a.status !== HighAvailabilityStatus.FAILED)) {
            return <Translate content="highAvailability.activities.operations.retryDisabledNonFailedActivities" />;
        }
        return <Translate content="highAvailability.activities.operations.retryDisabledTooltip" />;
    }, [canRetry, processSelection, selectedActivities]);

    const moveBackTooltip = React.useMemo((): React.ReactNode => {
        if (canMoveBack) {
            return <Translate content="highAvailability.activities.operations.moveBackConfirmation.tooltip" />;
        }
        if (processSelection.length === 0 && selectedActivities.length === 0) {
            return <Translate content="highAvailability.activities.operations.moveBackDisabledNothingSelected" />;
        }
        if (allSelectedEntities.every(p => isMoveBackTaskInProgress(p))) {
            return <Translate content="highAvailability.activities.operations.moveBackDisabledMigrationInProgress" />;
        }
        if (allSelectedEntities.every(p => p.status !== HighAvailabilityEntityStatus.COMPLETED)) {
            return <Translate content="highAvailability.activities.operations.moveBackDisabledNotCompleted" />;
        }
        return <Translate content="highAvailability.activities.operations.moveBackDisabledAllMigratedBack" />;
    }, [canMoveBack, processSelection, selectedActivities, allSelectedEntities]);

    const addProcessesToSelection = (activity: IHighAvailabilityActivity, processes: IHighAvailabilityActivityEntity[]) => {
        const newProcessSelection = [...processes, ...processSelection];
        setProcessSelection(newProcessSelection);
        const processIds = newProcessSelection.map((p: IHighAvailabilityActivityEntity) => p.id);
        // if all processes of the activity are selected, add the activity to selection
        if ((activity.entities?.data ?? []).every((p) => processIds.includes(p.id))) {
            if (!selection.includes(activity.id.toString())) {
                setSelection([...selection, activity.id.toString()]);
            }
        }
    };

    const removeProcessesFromSelection = (activity: IHighAvailabilityActivity, processes: IHighAvailabilityActivityEntity[]) => {
        const processIds = processes.map((p: IHighAvailabilityActivityEntity) => p.id);
        setProcessSelection(processSelection.filter(p => !processIds.includes(p.id)));
        setSelection(selection.filter((id: string) => id !== activity.id.toString()));
    };

    const handleSelection = (ids: string[]) => {
        const deselectedIds: string[] = selection.filter(id => !ids.includes(id));
        const deselectedProcessIds: number[] = [];
        activities.data.filter(activity => deselectedIds.includes(activity.id.toString()))
            .forEach(activity => {
                activity.entities?.data.forEach(p => deselectedProcessIds.push(p.id));
            });

        setSelection(ids);
        setProcessSelection(processSelection.filter(p => !deselectedProcessIds.includes(p.id)));
    };

    const handleMoveBack = async (processIds: number[]) => {
        await moveBackHighAvailabilityEntities({ entity_ids: processIds });
    };

    const handleMoveBackSelection = async () => {
        track(ANALYTICS_EVENTS.ADMIN.HIGH_AVAILABILITY_ACTIVITIES.MOVE_BACK_CLICKED);
        await moveBackHighAvailabilityEntities({
            high_availability_activity_ids: selection.map(Number),
            entity_ids: processSelection.map(p => p.id),
        });
    };

    const handleMoveBackActivity = React.useCallback(async (activityId: number) => {
        track(ANALYTICS_EVENTS.ADMIN.HIGH_AVAILABILITY_ACTIVITIES.MOVE_BACK_CLICKED);
        await moveBackHighAvailabilityEntities({
            high_availability_activity_ids: [activityId],
        });
    }, [moveBackHighAvailabilityEntities]);

    const handleStatusFilterChange = (option: PropsValue<ISelectRequiredOption>) => {
        const typedOption = option as ISelectRequiredOption;

        setSelectedStatus(typedOption);
        setFilters(prev => ({
            ...prev,
            status: typedOption ? typedOption.value : undefined,
        }));
    };

    const handleCrFilterChange = (option: PropsValue<IComputeResourceFilterOptionType>) => {
        const typedOption = option as IComputeResourceFilterOptionType;

        setSelectedCr(typedOption);
        setFilters(prev => ({
            ...prev,
            source_compute_resource_id: typedOption ? typedOption.value : undefined,
        }));
    };

    const list = React.useMemo(() => activities.data.map((activity: IHighAvailabilityActivity) => {
        const canMoveBackActivity = activity.entities === undefined
            || activity.entities.data.some(
                p => p.status === HighAvailabilityEntityStatus.COMPLETED
                    && !p.is_migrated_back
                    && !isMoveBackTaskInProgress(p)
            );

        const actionEl = (
            <ButtonWithConfirmation
                data-cy={dataCySelector(activity.id, HIGH_AVAILABILITY_HISTORY.ROW_MOVE_BACK_BUTTON)}
                icon={ICONS.ARROW_BACK}
                disabled={!canMoveBackActivity}
                isLoading={!!activity.is_loading}
                confirmationButtonGhost={true}
                buttonColor={COLORS.GREY_80}
                confirmationPopoverButtonColor={COLORS.RED_0}
                handleConfirm={() => handleMoveBackActivity(activity.id)}
                translations={{
                    title: <Translate content="highAvailability.activities.operations.moveBackConfirmation.title" />,
                    button: <Translate content="highAvailability.activities.operations.moveBackConfirmation.button" />,
                    text: <Translate content="highAvailability.activities.operations.moveBackConfirmation.text" />,
                    tooltip: <Translate content="highAvailability.activities.operations.moveBackButton" />,
                }}
            />
        );

        return {
            colId: activity.id,
            colComputeResource: (
                <Action
                    component={Link}
                    to={pathTo(`compute_resources/${activity.source_compute_resource.id}`)}
                >
                    {activity.source_compute_resource.name}
                </Action>
            ),
            colStatus: (
                <HighAvailabilityHistoryStatusComponent
                    status={activity.status}
                    compact={true}
                    entitiesCount={activity.entities_count}
                />
            ),
            colStartedAt: (
                <>
                    {activity.started_at && <CopyText>{formatTableDate(activity.started_at)}</CopyText>}
                    {DONE_STATUSES.includes(activity.status) && activity.ended_at && (
                        <CopyText>{formatTableDate(activity.ended_at)}</CopyText>
                    )}
                </>
            ),
            colDuration: formatDuration(activity.started_at, activity.ended_at),
            colProcesses: activity.entities_count.total,
            colFailoverDomain: activity.failover_domain
                ? (
                    <Action
                        component={Link}
                        to={pathTo('failover_domains/activities')}
                    >
                        {activity.failover_domain.name}
                    </Action>
                )
                : <>&mdash;</>,
            colActions: actionEl,
            key: activity.id.toString(),
        };
    }), [activities.data, handleMoveBackActivity]);

    const renderRow = (row: IListRow) => {
        const activity = activities.data.find(item => item.id.toString() === row.key);
        if (activity === undefined) {
            return;
        }

        return (
            <HighAvailabilityEntityRow
                isLoading={!!activity.is_loading}
                activity={activity}
                isActivitySelected={selection.includes(activity.id.toString())}
                handleAddProcessesToSelection={addProcessesToSelection}
                handleRemoveProcessesFromSelection={removeProcessesFromSelection}
                onMoveBack={handleMoveBack}
            />
        );
    };

    return (
        <HistoryTableWrapper>
            <Loader isLoading={isLoadingActivities && isFirstLoading}>
                <List
                    isLoading={isLoadingActivities}
                    isFirstLoading={isFirstLoading}
                    columns={columns}
                    data={list}
                    emptyView={emptyView}
                    filtered={!!filteredCount || isLoadingActivities}
                    selection={selection}
                    onSelectionChange={handleSelection}
                    onExpandedRowsChange={handleExpandedRowsChange}
                    expandedRows={expandedRows}
                    renderRowBody={renderRow}
                    toolbar={(
                        <HighAvailabilityOperations
                            canMoveBack={canMoveBack}
                            isMovingBack={isMovingBack}
                            canRetry={canRetry}
                            isRetrying={false}
                            onMoveBack={handleMoveBackSelection}
                            onRetry={() => {}}
                            retryTooltip={retryTooltip}
                            moveBackTooltip={moveBackTooltip}
                            selectedStatus={selectedStatus}
                            onSelectedStatusFilterChange={handleStatusFilterChange}
                            selectedCr={selectedCr}
                            onSelectedCrFilterChange={handleCrFilterChange}
                            filteredCount={filteredCount}
                            meta={activities.meta}
                            loadItems={loadPaginated}
                            isLoading={isLoadingActivities}
                        />
                    )}
                />
            </Loader>
        </HistoryTableWrapper>
    );
};

const mapStateToProps = (state: RootState) => ({
    activities: state.highAvailabilityActivities.list,
    isLoadingActivities: state.app.loadingFlags.has(LOADING_FLAGS.HIGH_AVAILABILITY_HISTORY),
    isMovingBack: state.app.loadingFlags.has(LOADING_FLAGS.HIGH_AVAILABILITY_MOVING_BACK),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    highAvailabilityActions: bindActionCreators(haActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HighAvailabilityHistoryTable));
